import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import type { ChartCell, ChartHalf, ChartOption, ChartRow } from '../../presenters/chart-of-accounts.presenter';
import { ChartDecision, chartCellKey } from './chart-of-accounts.types';

/** The picker panel's width (`.dh-picker__panel`), its distance to the trigger and to the window edge. */
const PICKER_WIDTH = 420;
const PICKER_GAP = 4;
const PICKER_MARGIN = 8;

/**
 * REV-22479 — the Kontenplan table (pure view).
 *
 * Two cells per account, position then function, and each works like a document's account cell in
 * the Dokumente hub: the violet AI state while the AI classifies, a proposal chip, the picker when
 * there is no value, and a proposal or decided value that opens the picker on click.
 *
 * Every choice is emitted at once — no confirm button, no batch. The container saves it.
 *
 * The picker is the Dokumente hub's: an amber trigger and a searchable panel in the flyout layer
 * (`hamFlyout`), so the table never cuts it off. One panel at a time.
 */
@Component({
    selector: 'abcp-chart-of-accounts',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './chart-of-accounts.component.html',
    styleUrls: ['./chart-of-accounts.component.scss'],
})
export class AbcpChartOfAccountsComponent implements OnDestroy {
    @Input() rows: readonly ChartRow[] = [];
    @Input() positionOptions: readonly ChartOption[] = [];
    @Input() loading = false;
    @Input() error = false;
    /** How many halves the AI is classifying right now — the notice above the table. */
    @Input() working = 0;
    /** Cells whose write is in flight (`chartCellKey`): their buttons are disabled. */
    @Input() pendingKeys: readonly string[] = [];
    /** Cells whose last write failed (`chartCellKey`). */
    @Input() failedKeys: readonly string[] = [];

    /** Whether accounts without a balance are listed too. Off: only the accounts that need a position. */
    @Input() withZeroAccounts = false;

    @Output() zeroAccountsToggle = new EventEmitter<void>();
    @Output() pick = new EventEmitter<ChartDecision>();

    /**
     * Cells that show the picker instead of their value — after a click on a proposal or a decided
     * value. Input state of this view only: nothing is saved until a value is picked.
     */
    private readonly editing = new Set<string>();

    public readonly skeletonRows = [1, 2, 3, 4, 5, 6];

    /** The cell whose panel is open (`chartCellKey`), its search term and where the panel sits. */
    public openKey: string | null = null;
    public pickerSearch = '';
    public pickerPosition: { top: number; left: number; right: number } | null = null;
    private pickerTrigger: HTMLElement | null = null;

    @ViewChild('pickerPanel') private pickerPanelRef?: ElementRef<HTMLElement>;

    private readonly onPickerAnchorMoved = (): void => this.placePicker();

    public constructor(private readonly cdr: ChangeDetectorRef) {}

    public trackByAccountId(_: number, row: ChartRow): string {
        return row.accountId;
    }

    public cellOf(row: ChartRow, half: ChartHalf): ChartCell {
        return half === 'position' ? row.position : row.function;
    }

    public optionsOf(row: ChartRow, half: ChartHalf): readonly ChartOption[] {
        return half === 'position' ? this.positionOptions : row.functionOptions;
    }

    /** The picker shows for a cell without a value, or one the user has opened. */
    public showsPicker(row: ChartRow, half: ChartHalf): boolean {
        const cell = this.cellOf(row, half);

        return (
            cell.state === 'open' || (this.editing.has(chartCellKey(row.accountId, half)) && cell.state !== 'working')
        );
    }

    public isPending(row: ChartRow, half: ChartHalf): boolean {
        return this.pendingKeys.includes(chartCellKey(row.accountId, half));
    }

    public hasFailed(row: ChartRow, half: ChartHalf): boolean {
        return this.failedKeys.includes(chartCellKey(row.accountId, half));
    }

    /** A click on a proposal or a decided value: the picker opens; nothing is saved yet. */
    public onOpenPicker(row: ChartRow, half: ChartHalf): void {
        this.editing.add(chartCellKey(row.accountId, half));
    }

    /** Back to the value without a change — only for a cell that has one. */
    public onCancelPicker(row: ChartRow, half: ChartHalf): void {
        this.editing.delete(chartCellKey(row.accountId, half));
    }

    public isPickerOpen(row: ChartRow, half: ChartHalf): boolean {
        return this.openKey === chartCellKey(row.accountId, half);
    }

    /** What the open panel lists: the cell's options, narrowed by the search term. */
    public filteredOptions(row: ChartRow, half: ChartHalf): readonly ChartOption[] {
        const term = this.pickerSearch.trim().toLowerCase();
        const options = this.optionsOf(row, half);

        return term === '' ? options : options.filter((option) => option.label.toLowerCase().includes(term));
    }

    /** Opening a panel closes any other — two open panels would overlap. */
    public onPickerToggle(row: ChartRow, half: ChartHalf, trigger: HTMLElement): void {
        if (this.isPickerOpen(row, half)) {
            this.closePicker();
            return;
        }
        this.closePicker();
        this.openKey = chartCellKey(row.accountId, half);
        this.pickerTrigger = trigger;
        this.placePicker();
        window.addEventListener('scroll', this.onPickerAnchorMoved, true);
        window.addEventListener('resize', this.onPickerAnchorMoved);
    }

    public onPick(row: ChartRow, half: ChartHalf, value: string): void {
        this.closePicker();
        this.editing.delete(chartCellKey(row.accountId, half));
        this.pick.emit({ accountId: row.accountId, half, value });
    }

    public closePicker(): void {
        this.openKey = null;
        this.pickerSearch = '';
        this.pickerTrigger = null;
        this.pickerPosition = null;
        window.removeEventListener('scroll', this.onPickerAnchorMoved, true);
        window.removeEventListener('resize', this.onPickerAnchorMoved);
    }

    /** A click anywhere outside closes the panel — the panel stops propagation on its own subtree. */
    @HostListener('document:click')
    public onDocumentClick(): void {
        if (this.openKey !== null) {
            this.closePicker();
            this.cdr.markForCheck();
        }
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        this.onDocumentClick();
    }

    public ngOnDestroy(): void {
        this.closePicker();
    }

    /**
     * Below the trigger; above it when it would leave the window there and fits above. Kept inside the
     * window horizontally. The same placement as the Dokumente hub's picker.
     */
    private placePicker(): void {
        const trigger = this.pickerTrigger;
        if (trigger === null || this.openKey === null) {
            return;
        }
        const rect = trigger.getBoundingClientRect();
        const height = this.pickerPanelRef?.nativeElement.offsetHeight ?? 0;
        const below = rect.bottom + PICKER_GAP;
        const above = rect.top - PICKER_GAP - height;
        const top =
            height > 0 && below + height > window.innerHeight - PICKER_MARGIN && above >= PICKER_MARGIN ? above : below;
        const left = Math.max(PICKER_MARGIN, Math.min(rect.left, window.innerWidth - PICKER_WIDTH - PICKER_MARGIN));

        this.pickerPosition = { top, left, right: window.innerWidth - left - PICKER_WIDTH };
        this.cdr.markForCheck();
    }
}
